import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { showNotification } from "jazasoft";

import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSafeState, useSetting, useUnit } from "../../../hooks";
import { getDistinctValues, getStyleColor, isEmpty, listFromCsv, toFixed, toFixedValue } from "../../../utils/helpers";
import { getEffPartList, getEffectiveExtraPercent } from "../../../helpers/orderHelper";

import SettingKeys from "../../../utils/SettingKeys";
import { getCutOrderRows } from "../cutPlanHelper";
import { LotType, OrderFlowType, UOM } from "../../../constants";
import { convert } from "../../../helpers/unitConversionHelper";

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const csvField = (flowInfoList, field) => getDistinctValues(flowInfoList.map(fi => fi[field]).filter(e => !isEmpty(e))).join(", ");

const sanitizeFlowInfo = flowInfoList => {
  if (isEmpty(flowInfoList)) return {};
  const flowInfo = flowInfoList[0];
  const units = getDistinctValues(flowInfoList.map(e => e.primaryUnitId));
  const fpos = getDistinctValues(flowInfoList.map(e => e.fpo));
  return {
    product: flowInfo.product,
    productName: flowInfo.product?.name,
    style: getDistinctValues(flowInfoList.map(fi => fi.style)).join(", "),
    color: getDistinctValues(flowInfoList.map(fi => fi.color)).join(", "),
    unitId: units?.length === 1 ? units[0] : null,
    fpo: fpos?.length === 1 ? fpos[0] : null,
    ...["inseam", "destination", "deliveryDate", "delMode", "customerFlowRef", "flowRef", "itemCode", "fabColor"].reduce(
      (acc, field) => ({ ...acc, [field]: csvField(flowInfoList, field) }),
      {}
    ),
    orderQty: flowInfoList.reduce((acc, fi) => acc + fi.orderQty, 0),
    fpoQty: flowInfoList.reduce((acc, fi) => acc + (fi.fpoQty || 0), 0),
    extra: getEffectiveExtraPercent(flowInfoList),
    ledgerList: flowInfoList.flatMap(fi => fi.ledgerList || []),
    fabricId: flowInfo?.fabricId
  };
};

const useCutOrder = props => {
  const id = props.match?.params?.id;

  const [version, setVersion] = useSafeState(1);
  const [tabIdx, setTabIdx] = useSafeState(0);
  const [selectedKeys, setSelectedKeys] = useSafeState([]); // For Tab: Ready To Plan
  const [errorMessage, setErrorMessage] = useSafeState(null);

  const history = useHistory();
  const dispatch = useDispatch();
  const { allUnitList } = useUnit();

  const [mutate, { loading: busy }] = useMutation();

  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);
  const conversionFactor = useSetting(SettingKeys.UOM_CONVERSION_FACTOR);
  const conversionFactorList = JSON.parse(conversionFactor || "[]");

  const { data: order = {}, loading } = useQuery({ url: `orders/${id}`, params: { page: "cutPlan" }, version });
  const { data: fpoList = [] } = useQuery({ url: `fpo`, params: { search: `orderId==${id}`, size: 2000 }, version });
  const { data: ledgerData = [] } = useQuery(id && { url: `ledgers`, params: { search: `order.id==${id}`, size: 10000 }, version });

  const fabricItemIds = getDistinctValues(ledgerData.map(l => l.fabricItem?.id).filter(id => !isEmpty(id)));

  const { data: rollInfoList = [] } = useQuery(
    !isEmpty(fabricItemIds) && {
      url: `rolls`,
      params: { search: `fabricItem.id=in=(${fabricItemIds.join(",")});parentId=='NULL'`, size: 100000 },
      version
    }
  );

  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(order)) return [];

    let rowList = [];
    if (tabIdx === 0) {
      rowList = getCutOrderRows(order, productParts, LotType.PRE_RUN);
      rowList = rowList
        .map(row => {
          const cutOrder = row.cutOrder;
          const styleColorList = cutOrder?.styleColorList || [];
          const ledgerList = ledgerData
            .filter(
              l =>
                cutOrder.productIdList?.includes(l.productId) &&
                styleColorList.includes(getStyleColor(l.style, l.styleColor)) &&
                l.part === cutOrder.part
            )
            .filter(l => isEmpty(cutOrder.parentColor) || cutOrder.parentColor === l.parentStyleColor);
          const ledger = !isEmpty(ledgerList) ? ledgerList.find(e => e.style === row.style && e.styleColor === row.color) : {};

          const allocatedRollList = rollInfoList.filter(ri => ri.cutOrderFlowId === cutOrder?.id);

          const uom = row?.uom;

          const allocatedLength = allocatedRollList.reduce((acc, ri) => acc + ri.length, 0);
          const allocatedWeight = allocatedRollList.reduce((acc, ri) => acc + (ri.weight || 0), 0);
          const allocatedFabric =
            uom === UOM.KILOGRAM.value
              ? allocatedWeight
              : convert({ input: allocatedLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });

          const allocated = isEmpty(row.parentKey) ? Number(toFixed(allocatedFabric, 1)) : null;

          return {
            ...row,
            itemCode: ledger?.fabric?.itemCode,
            fabColor: ledger?.fabColor,
            fabricId: ledger?.fabric?.id,
            allocatedRollList,
            allocated,
            status: !isEmpty(allocated) ? cutOrder.state : null
          };
        })
        .sort((a, b) => b.cutOrder?.id - a.cutOrder?.id)
        .map(row => ({ ...row, poRef: order?.poRef, customerName: order?.customer?.name, seasonName: order?.season?.name }));
    } else {
      rowList = fpoList
        .flatMap(fpo => {
          const flowInfoList = order?.flowInfoList?.filter(fi => fpo.flowInfoIdList.includes(fi.id)) || [];
          const productList = listFromCsv(fpo.products);
          const styleList = listFromCsv(fpo.styles);
          const colorList = listFromCsv(fpo.colors);

          return productList
            .flatMap(product => {
              return styleList.flatMap(style => {
                return colorList.flatMap(color => {
                  const styleColor = getStyleColor(style, color);
                  const fiList = flowInfoList
                    .filter(fi => fi.product?.name === product && fi.style === style && fi.color === color)
                    .flatMap(fi => {
                      let sizeBreakupList = fi.sizeBreakupList || [];
                      const sizeGroups = getDistinctValues(sizeBreakupList.map(sb => sb.sizeGroup).filter(e => !isEmpty(e)));
                      let sbList = fi.sizeBreakupList.filter(sb => sb.type === "ORDER");
                      if (isEmpty(sbList)) {
                        sbList = order.flowInfoList.filter(e => fi.orderParentId === e.id).flatMap(e => e.sizeBreakupList || []);
                      }
                      if (!isEmpty(fi.inseam) || isEmpty(sizeGroups) || sizeGroups?.length <= 1) {
                        return [{ ...fi, orderQty: sbList.reduce((acc, sb) => acc + sb.qty, 0), sizeBreakupList: sbList }];
                      } else {
                        return sizeGroups.map(sizeGroup => {
                          let sbList = fi.sizeBreakupList.filter(sb => sb.type === "ORDER");
                          if (isEmpty(sbList)) {
                            sbList = order.flowInfoList.filter(e => fi.orderParentId === e.id).flatMap(e => e.sizeBreakupList || []);
                          }
                          sbList = sbList.filter(sb => sb.sizeGroup === sizeGroup);
                          const fpoSbList = fi.fpoSizeBreakupList?.filter(sb => sb.sizeGroup === sizeGroup);
                          const orderQty = sbList.reduce((acc, sb) => acc + sb.qty, 0);
                          const fpoQty = fpoSbList.reduce((acc, sb) => acc + sb.qty, 0);
                          return { ...fi, inseam: sizeGroup, orderQty, fpoQty, sizeBreakupList: sbList, fpoSizeBreakupList: fpoSbList };
                        });
                      }
                    });
                  const partList = getEffPartList(fiList, order);
                  return partList.flatMap(part => {
                    const parentKey = `${fiList.map(e => e.id).join("-")}-${part.effPartName}`;
                    const finalFlowInfoList = fiList.map(flowInfo => {
                      const ledgerList = ledgerData.filter(
                        l =>
                          l.productId === flowInfo?.product?.id &&
                          l.style === flowInfo.style &&
                          l.styleColor === flowInfo.color &&
                          l.part === part.effPartName
                      );
                      const ledger = ledgerList[0];
                      return {
                        ...flowInfo,
                        ledgerList,
                        itemCode: ledger?.fabric?.itemCode,
                        fabColor: ledger?.fabColor,
                        fabricId: ledger?.fabric?.id
                      };
                    });

                    if (finalFlowInfoList?.length > 1) {
                      let parent = { ...sanitizeFlowInfo(finalFlowInfoList), key: parentKey, effPartName: part.effPartName, bomCu: part?.bomCu };
                      return [
                        parent,
                        ...finalFlowInfoList.map(fi => {
                          const cutOrderList = order.orderFlowList
                            .filter(of => of.type === OrderFlowType.CUTPLAN && of.lotType === LotType.PRE_RUN)
                            .filter(of => of.part === part.effPartName)
                            .filter(of => of.productIdList?.includes(fi.product?.id))
                            .filter(of => of.styleColorList?.includes(styleColor))
                            .filter(of => isEmpty(fi.inseam) || listFromCsv(of.inseams).includes(fi.inseam))
                            .filter(of => isEmpty(fi.destination) || listFromCsv(of.destinations).includes(fi.destination))
                            .filter(of => isEmpty(fi.deliveryDate) || listFromCsv(of.deliveries).includes(fi.deliveryDate))
                            // .filter(of => isEmpty(fi.delMode) || listFromCsv(of.delModes).includes(fi.delMode))
                            .filter(of => isEmpty(fi.flowRef) || listFromCsv(of.flowRefs).includes(fi.flowRef))
                            .filter(of => isEmpty(fi.customerFlowRef) || listFromCsv(of.customerFlowRefs).includes(fi.customerFlowRef));
                          const lastLot = isEmpty(cutOrderList) ? 0 : cutOrderList.sort((a, b) => b.lot - a.lot)[0]?.lot || 0;
                          return {
                            ...fi,
                            parentKey,
                            key: `${fi.id}-${fi.inseam || ""}-${part.effPartName}`,
                            effPartName: part.effPartName,
                            bomCu: part?.bomCu,
                            cutOrderList,
                            lot: lastLot + 1,
                            lotValue: `Pre Run ${lastLot + 1}`,
                            productName: fi.product?.name,
                            unitId: fpo.unitId || fpo.primaryUnitId
                          };
                        })
                      ];
                    } else {
                      const cutOrderList = order.orderFlowList
                        .filter(of => of.type === OrderFlowType.CUTPLAN && of.lotType === LotType.PRE_RUN)
                        .filter(of => of.part === part.effPartName)
                        .filter(of => of.productIdList?.includes(finalFlowInfoList[0].product?.id))
                        .filter(of => of.styleColorList?.includes(styleColor));
                      const lastLot = isEmpty(cutOrderList) ? 0 : cutOrderList.sort((a, b) => b.lot - a.lot)[0]?.lot || 0;

                      return finalFlowInfoList.map(fi => ({
                        ...fi,
                        key: `${fi.id}-${part.effPartName}`,
                        effPartName: part.effPartName,
                        cutOrderList,
                        lot: lastLot + 1,
                        lotValue: `Pre Run ${lastLot + 1}`,
                        bomCu: part?.bomCu,
                        unitId: fpo.unitId || fpo.primaryUnitId,
                        productName: fi.product?.name
                      }));
                    }
                  });
                });
              });
            })
            .map(row => ({ ...row, poRef: order?.poRef, customerName: order?.customer?.name, seasonName: order?.season?.name }));
        })
        .filter(flowInfo => !isEmpty(flowInfo.itemCode) && !isEmpty(flowInfo.fabColor));
    }

    return rowList;
  }, [order, fpoList, tabIdx, ledgerData, rollInfoList, productParts, conversionFactorList]);

  const flowInfoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "unitId", title: "Unit", render: value => allUnitList?.find(e => e.unitId === value)?.name || value, highlight: true },
      { dataKey: "fpo", title: "FPO", highlight: true },
      { dataKey: "externalFpo", title: "Ext. FPO", highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "effPartName", title: "Part", highlight: true },
      { dataKey: "lotValue", title: "Lot", highlight: true },
      { dataKey: "inseam", title: "Inseam", filterOptions: { filter: true }, highlight: true },
      { dataKey: "destination", title: "Destination", filterOptions: { filter: true }, highlight: true },
      { dataKey: "deliveryDate", title: "Delivery Date", filterOptions: { filter: true }, highlight: true },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "fpoQty", title: "FPO Qty" },
      { dataKey: "extra", title: "Extra \nShip (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      {
        dataKey: "ocu",
        title: "BOM CU",
        render: (value, _, row) => (!isEmpty(value) && value > 0 ? `${toFixedValue(value, 3)} ${UomCodeFromValue[row.uom]}` : "")
      },
      {
        dataKey: "itemCode",
        title: "Item Code",
        linkPath: row => history.push(`/warehouse/fabrics/${row?.fabricId}/view`)
      },
      { dataKey: "fabColor", title: "Fabric Color" },
      { dataKey: "allocated", title: "Alloc. Fabric", align: "right", render: (value, _, row) => `${value || 0} ${UomCodeFromValue[row.uom]}` },
      { dataKey: "status", title: "Status" }
    ];

    if (flowInfoRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (flowInfoRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (flowInfoRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (flowInfoRows.every(e => isEmpty(e.fpo))) {
      cols = cols.filter(c => c.dataKey !== "fpo");
    }
    if (flowInfoRows.every(e => isEmpty(e.externalFpo))) {
      cols = cols.filter(c => c.dataKey !== "externalFpo");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => !["unitId", "fpo", "externalFpo", "fpoQty"].includes(c.dataKey));
    }
    if (tabIdx === 1) {
      cols = cols.filter(c => !["allocated", "status"].includes(c.dataKey));
    }

    return cols;
  }, [tabIdx, flowInfoRows, allUnitList]);

  //** Redirect to Planning Tab if Cut Order not created yet **/
  useDeepEffect(() => {
    if (isEmpty(order) || loading) return;
    if (tabIdx === 0 && isEmpty(flowInfoRows)) {
      setTabIdx(1);
    }
  }, [order, loading]);

  const selectedRows = tabIdx === 0 ? [] : flowInfoRows?.filter(fi => selectedKeys?.includes(fi.key)) || [];

  useDeepEffect(() => {
    if (isEmpty(selectedRows)) {
      setErrorMessage("");
    } else {
      const fpoList = getDistinctValues(selectedRows.map(e => e.fpo).filter(fpo => !isEmpty(fpo)));
      if (fpoList?.length > 1) {
        setErrorMessage("Multiple FPOs cannot be combined together for making Cut Order");
      } else {
        setErrorMessage("");
      }
    }
  }, [selectedRows]);

  const createCutOrder = () => {
    const parentKeys = getDistinctValues(selectedRows.map(e => e.parentKey).filter(key => !isEmpty(key)));
    const flowInfoList = selectedRows.filter(row => isEmpty(parentKeys) || !parentKeys.includes(row.key)).map(({ key, parentKey, ...fi }) => fi);
    const options = {
      url: `cut-orders`,
      method: "post",
      params: { orderId: id, lotType: LotType.PRE_RUN },
      data: { effPartName: flowInfoList[0]?.effPartName, lot: flowInfoList[0]?.lot, flowInfoList }
    };
    mutate(options, {
      onSuccess: () => {
        dispatch(showNotification("Cut Order created successfully."));
        setVersion(v => v + 1);
        setTabIdx(0);
      }
    });
  };

  const onInlineActionClick = (event, action, row) => {
    if (action === "delete") {
      if (isEmpty(row.cutOrder)) return;
      const options = { url: `cut-orders`, method: "delete", params: { orderId: id, cutOrderId: row.cutOrder?.id } };
      mutate(options, {
        onSuccess: () => {
          dispatch(showNotification("Cut Order deleted successfully."));
          setVersion(v => v + 1);
        }
      });
    }
  };

  return {
    busy,
    version,
    errorMessage,
    selectedKeys,
    order,
    flowInfoColumns,
    flowInfoRows,
    tabIdx,
    setTabIdx,
    setSelectedKeys,
    onInlineActionClick,
    createCutOrder
  };
};

export default useCutOrder;
